/**
 * AdminTicketCatalogPage - 수강권 카탈로그(폼 2-A) 페이지
 *
 * /centers/:centerId/ticket-catalog 경로로 접근하며,
 * Firestore에 저장된 카탈로그(센터가 판매하는 수강권 종류)를 조회하고,
 * "수강권 등록" 버튼으로 신규 카탈로그를 추가한다.
 *
 * 회원 등록(Step 6 — AdminMembersListPage) 패턴을 따른다.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import { useCenterContext } from "@/contexts/CenterContext";
import { CreateTicketCatalogModal } from "@/components/CreateTicketCatalogModal";
import { EditTicketCatalogModal } from "@/components/EditTicketCatalogModal";
import { useDeleteTicketCatalog } from "@/hooks/useTicketCatalog";
import {
  getAdminTicketCatalogList,
  type TicketCatalogItem,
} from "@/services/firestore/admin-ticket-catalog";

export function AdminTicketCatalogPage() {
  const { centerId } = useParams<{ centerId: string }>();
  const user = useCurrentUser();
  const { currentCenter } = useCenterContext();

  const [items, setItems] = useState<TicketCatalogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [editTarget, setEditTarget] = useState<TicketCatalogItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const deleteTicketCatalog = useDeleteTicketCatalog();

  const fetchItems = useCallback(async () => {
    if (!centerId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await getAdminTicketCatalogList(user.uid, Number(centerId));
      setItems(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error("수강권 카탈로그 조회 실패"));
    } finally {
      setIsLoading(false);
    }
  }, [user.uid, centerId]);

  useEffect(() => {
    void fetchItems();
  }, [fetchItems]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.trim().toLowerCase();
    return items.filter((t) => t.title.toLowerCase().includes(q));
  }, [items, searchQuery]);

  const handleDelete = async (item: TicketCatalogItem) => {
    if (!centerId) return;
    const confirmed = window.confirm(
      `수강권 "${item.title}"을(를) 삭제하시겠습니까?\n이미 구매된 회원 수강권은 영향받지 않으며, 목록에서만 제외됩니다.`,
    );
    if (!confirmed) return;

    setActionError(null);
    try {
      await deleteTicketCatalog.mutateAsync({ centerId: Number(centerId), ticketId: item.id });
      await fetchItems();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : "수강권 삭제에 실패했습니다.");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">{error.message}</p>
          <button
            onClick={() => {
              setError(null);
              void fetchItems();
            }}
            className="mt-3 px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 센터 상세
        </Link>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">수강권 카탈로그 ({items.length})</h1>
          <p className="text-sm text-gray-500 mt-1">
            센터가 판매하는 수강권 종류를 등록·확인합니다.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setCreateSuccess(false);
              setIsCreateOpen(true);
            }}
            disabled={!currentCenter}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            수강권 등록
          </button>
        </div>
      </div>

      {createSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <p className="text-sm text-green-700">수강권이 등록되었습니다.</p>
        </div>
      )}

      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      <div>
        <input
          type="text"
          placeholder="수강권명 검색"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-sm px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {items.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700">
            등록된 수강권이 없습니다. "수강권 등록" 버튼으로 추가하세요.
          </p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-500">검색 결과가 없습니다.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  수강권명
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  총 횟수
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용기한
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  최대 인원
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.title}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.type === "P" ? "P (Private)" : "G (Group)"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.totalCoupon}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.validDays != null ? `${item.validDays}일` : "무제한"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {item.maxTrainee ?? "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <button
                      aria-label={`${item.title} 수정`}
                      onClick={() => {
                        setCreateSuccess(false);
                        setActionError(null);
                        setEditTarget(item);
                      }}
                      className="text-blue-600 hover:text-blue-800 mr-4"
                    >
                      수정
                    </button>
                    <button
                      aria-label={`${item.title} 삭제`}
                      onClick={() => void handleDelete(item)}
                      disabled={deleteTicketCatalog.isPending}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50"
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {currentCenter && (
        <CreateTicketCatalogModal
          isOpen={isCreateOpen}
          centerName={currentCenter.name}
          onClose={() => setIsCreateOpen(false)}
          onCreated={async () => {
            setIsCreateOpen(false);
            setCreateSuccess(true);
            await fetchItems();
          }}
        />
      )}

      {editTarget && centerId && (
        <EditTicketCatalogModal
          isOpen={editTarget !== null}
          centerId={Number(centerId)}
          ticket={editTarget}
          onClose={() => setEditTarget(null)}
          onUpdated={async () => {
            setEditTarget(null);
            await fetchItems();
          }}
        />
      )}
    </div>
  );
}
